// Suivi de la facturation (Pilotage) — quels chantiers attendent une facture.
//
// Ce module ne fait que LIRE et assembler : il va chercher chantiers, agenda,
// devis, factures et clients, puis délègue tout le raisonnement à
// @imp/shared/suivi-facturation, partagé et testé à part.

import { type LigneFacturation, suiviFacturation } from '@imp/shared/suivi-facturation';

import { totalOrigineAcompte } from '@/lib/acompte-origine';
import { lireTout } from '@/lib/pagination';
import { supabase } from '@/lib/supabase';
import { type Contact, contactDisplayName, ORG_ID } from '@/modules/clients/api';

/** Une ligne du tableau, complétée des noms de clients pour l'affichage. */
export interface LigneFacturationAffichee extends LigneFacturation {
  /** Le client du chantier : chez qui les travaux se font. */
  client: string;
  /**
   * Le client à qui la facture ira, quand ce n'est PAS celui du chantier
   * (sous-traitance). Null quand c'est le même — rien à préciser.
   */
  clientFacture: string | null;
}

export async function listSuiviFacturation(): Promise<LigneFacturationAffichee[]> {
  // Tout est lu par tranches : ces tables couvrent l'historique complet, et une
  // lecture tronquée ferait disparaître des chantiers du suivi SANS erreur —
  // un chantier dont l'intervention n'aurait pas été lue passerait pour non
  // placé, et sortirait silencieusement de la liste « à facturer ».
  const [chantiers, interventions, factures, devis, contacts] = await Promise.all([
    lireTout<{
      id: string;
      numero: string;
      libelle: string;
      statut: string;
      contact_id: string | null;
      montant_total_ht: number | string;
    }>((de, a) =>
      supabase
        .from('chantiers')
        .select('id,numero,libelle,statut,contact_id,montant_total_ht')
        .eq('organization_id', ORG_ID)
        .is('deleted_at', null)
        .order('id')
        .range(de, a),
    ),
    lireTout<{ chantier_id: string | null; fin_at: string }>((de, a) =>
      supabase
        .from('evenements')
        .select('chantier_id,fin_at')
        .eq('organization_id', ORG_ID)
        .is('deleted_at', null)
        .not('chantier_id', 'is', null)
        // Une intervention annulée ne place plus le chantier.
        .neq('statut', 'annule')
        .order('id')
        .range(de, a),
    ),
    lireTout<{
      id: string;
      chantier_id: string | null;
      contact_id: string | null;
      numero: string | null;
      kind: string;
      statut: string;
      facture_liee_id: string | null;
      montant_ht: number | string;
      montant_ttc: number | string;
      montant_paye: number | string;
      metadata: unknown;
    }>((de, a) =>
      supabase
        .from('factures')
        // `facture_liee_id` : la facture qu'un avoir annule. Sans lui, une
        // annulation se compterait deux fois — la facture sort du calcul ET
        // l'avoir se déduit — et le déjà-facturé partirait dans le négatif.
        .select(
          'id,chantier_id,contact_id,numero,kind,statut,facture_liee_id,montant_ht,montant_ttc,montant_paye,metadata',
        )
        .eq('organization_id', ORG_ID)
        .is('deleted_at', null)
        .not('chantier_id', 'is', null)
        .order('id')
        .range(de, a),
    ),
    // Les devis disent trois choses : que le CRM suit ce chantier, que le
    // client a dit oui, et COMBIEN il a accepté — le montant engagé, que la
    // fiche chantier ne porte pas.
    lireTout<{
      id: string;
      chantier_id: string | null;
      contact_id: string | null;
      numero: string | null;
      statut: string;
      montant_ht: number | string;
      montant_ttc: number | string;
    }>((de, a) =>
      supabase
        .from('devis')
        .select('id,chantier_id,contact_id,numero,statut,montant_ht,montant_ttc')
        .eq('organization_id', ORG_ID)
        .is('deleted_at', null)
        .not('chantier_id', 'is', null)
        .order('id')
        .range(de, a),
    ),
    lireTout<Pick<Contact, 'id' | 'type_personne' | 'nom' | 'prenom' | 'raison_sociale'>>((de, a) =>
      supabase
        .from('contacts')
        .select('id,type_personne,nom,prenom,raison_sociale')
        .order('id')
        .range(de, a),
    ),
  ]);

  const parId = new Map(contacts.map((c) => [c.id, c]));
  const nom = (id: string | null): string => {
    if (!id) return 'sans client';
    const c = parId.get(id);
    return c ? contactDisplayName(c) : 'client inconnu';
  };

  const lignes = suiviFacturation(
    chantiers.map((c) => ({
      id: c.id,
      numero: c.numero,
      libelle: c.libelle,
      statut: c.statut,
      contactId: c.contact_id,
      montantTotalHt: Number(c.montant_total_ht),
    })),
    interventions.map((e) => ({ chantierId: e.chantier_id, finAt: e.fin_at })),
    factures.map((f) => ({
      id: f.id,
      chantierId: f.chantier_id,
      contactId: f.contact_id,
      // Un brouillon n'a pas encore de numéro : il en faut pourtant un à
      // afficher, sinon la pièce apparaît sans nom dans le détail de la ligne.
      numero: f.numero ?? 'brouillon',
      kind: f.kind,
      statut: f.statut,
      factureLieeId: f.facture_liee_id,
      montantHt: Number(f.montant_ht),
      montantTtc: Number(f.montant_ttc),
      montantPaye: Number(f.montant_paye),
      montantOrigineHt: totalOrigineAcompte(f.metadata),
    })),
    devis.map((d) => ({
      id: d.id,
      chantierId: d.chantier_id,
      contactId: d.contact_id,
      numero: d.numero ?? 'brouillon',
      statut: d.statut,
      montantHt: Number(d.montant_ht),
      montantTtc: Number(d.montant_ttc),
    })),
    Date.now(),
  );
  return lignes.map((l) => ({
    ...l,
    client: nom(l.contactId),
    // `contactFactureId` ne vaut quelque chose que s'il DIFFÈRE du client du
    // chantier : la fonction partagée a déjà tranché, on ne refait pas la
    // comparaison ici.
    clientFacture: l.contactFactureId === null ? null : nom(l.contactFactureId),
  }));
}
